import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaStar, FaInfoCircle, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import { Table, Pagination, Button, Collapse, Spinner } from 'react-bootstrap';

const VoteList = ({ symbol }) => {
  const [votes, setVotes] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [votesPerPage] = useState(10);
  const [error, setError] = useState(null);
  const [voteCost, setVoteCost] = useState(null);
  const [isTableVisible, setIsTableVisible] = useState(false);
  const [rating, setRating] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [voteMessage, setVoteMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const cleanSymbol = symbol.split(/[-_]/)[0].toUpperCase();

  useEffect(() => {
    const fetchVotes = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await axios.get(`/api/votes/${cleanSymbol}`);
        const fetchedVotes = response.data.votes ? response.data.votes : response.data;

        if (fetchedVotes.length > 0) {
          const sortedVotes = fetchedVotes.sort(
            (a, b) => new Date(b.transaction_created_at) - new Date(a.transaction_created_at)
          );

          setVotes(sortedVotes.slice(0, 100)); // Limita a 100 votos
          const totalVotes = sortedVotes.length;
          const totalRating = sortedVotes.reduce((acc, vote) => acc + vote.rating, 0);
          const average = totalVotes > 0 ? totalRating / totalVotes : 0;
          setAverageRating(average);
        } else {
          setVotes([]);
          setAverageRating(0);
          setError('No votes for this symbol. Be the first to vote!');
        }
      } catch (error) {
        console.error('Error fetching votes:', error);
        setError('An error occurred while fetching votes. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    const fetchVoteCost = async () => {
      try {
        const response = await axios.get(`/api/votes/cost/${cleanSymbol}`);
        setVoteCost(parseFloat(response.data.voteCost).toFixed(8));
      } catch (error) {
        console.error('Error fetching vote cost:', error);
        setVoteCost(null);
      }
    };

    fetchVotes();
    fetchVoteCost();
  }, [cleanSymbol]);

  const indexOfLastVote = currentPage * votesPerPage;
  const indexOfFirstVote = indexOfLastVote - votesPerPage;
  const currentVotes = votes.slice(indexOfFirstVote, indexOfLastVote);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleVoteSubmit = async () => {
    setIsSubmitting(true);
    setVoteMessage('');
    setError(null);

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        setVoteMessage(
          <span>
            <FaExclamationTriangle className="text-warning me-2" />
            You need to log in to vote.
          </span>
        );
        setIsSubmitting(false);
        return;
      }

      const response = await axios.post(
        '/api/votes/vote',
        { symbol: cleanSymbol, rating },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const { amountBurned } = response.data;

      setVoteMessage(
        <span>
          <FaCheckCircle className="text-success me-2" />
          Your vote has been successfully recorded! You burned or spent{' '}
          <strong>{parseFloat(amountBurned).toFixed(8)}</strong> FUEC.
        </span>
      );

      setVotes([...votes, { symbol: cleanSymbol, rating }]);
      const totalVotes = votes.length + 1;
      const totalRating = votes.reduce((acc, vote) => acc + vote.rating, 0) + rating;
      setAverageRating(totalRating / totalVotes);
    } catch (error) {
      console.error('Error submitting vote:', error);
      if (error.response) {
        if (error.response.status === 401) {
          setVoteMessage(
            <span>
              <FaExclamationTriangle className="text-warning me-2" />
              You are not authorized to vote. Please log in.
            </span>
          );
        } else if (error.response.data.error === 'Insufficient balance to complete the vote.') {
          setVoteMessage(
            <span>
              <FaExclamationTriangle className="text-warning me-2" />
              You do not have enough balance to submit your vote.
            </span>
          );
        } else {
          setVoteMessage(
            <span>
              <FaExclamationTriangle className="text-danger me-2" />
              An error occurred while submitting your vote. Please try again.
            </span>
          );
        }
      } else {
        setVoteMessage(
          <span>
            <FaExclamationTriangle className="text-danger me-2" />
            Network error. Please check your connection and try again.
          </span>
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="vote-list-container">
      <div className="vote-list">
        {isLoading ? (
          <Spinner animation="border" role="status">
            <span className="sr-only"></span>
          </Spinner>
        ) : (
          <>
            <div className="vote-section">
              <h6>
                Rate this coin (Average: {averageRating.toFixed(2)} / 5) Total Votes: {votes.length}
              </h6>
              {voteCost && (
                <p className="text-info">
                  <FaInfoCircle className="me-2" />
                  Voting for this coin will cost approximately <strong>{voteCost}</strong> FUEC.
                </p>
              )}
              <RatingStars
                averageValue={averageRating}
                userValue={rating}
                count={5}
                size={30}
                activeColor="#ffd700"
                onChange={(newRating) => setRating(newRating)}
              />

              <div className="button-container d-flex justify-content-center align-items-center mt-3">
                <button
                  onClick={handleVoteSubmit}
                  disabled={isSubmitting || rating === 0}
                  className="btn btn-primary me-2"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Vote'}
                </button>
                <Button
                  onClick={() => setIsTableVisible(!isTableVisible)}
                  aria-controls="vote-table"
                  aria-expanded={isTableVisible}
                  className="btn-secondary"
                >
                  {isTableVisible ? 'Hide Transactions' : 'Show Transactions'}
                </Button>
              </div>

              {voteMessage && <p className="vote-message mt-2">{voteMessage}</p>}
              {error && (
                <p className="text-danger mt-2">
                  <FaExclamationTriangle className="me-2" />
                  {error}
                </p>
              )}
            </div>

            <Collapse in={isTableVisible}>
              <div id="vote-table">
                <Table striped bordered hover responsive className="mt-3">
                  <thead>
                    <tr>
                      <th>Rating</th>
                      <th>Crypto Address</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Transaction</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentVotes.length > 0 ? (
                      currentVotes.map((vote, index) => (
                        <tr key={index}>
                          <td>
                            <FaStar color="#ffd700" /> {vote.rating || 'N/A'}
                          </td>
                          <td>{vote.crypto_address || 'N/A'}</td>
                          <td>{vote.amount ? parseFloat(vote.amount).toFixed(8) : 'N/A'}</td>
                          <td>{vote.status || 'N/A'}</td>
                          <td>{vote.transaction_id || 'N/A'}</td>
                          <td>
                            {vote.transaction_created_at
                              ? new Date(vote.transaction_created_at).toLocaleString()
                              : 'N/A'}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center">
                          <FaInfoCircle className="me-2 text-info" />
                          No transactions found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                <Pagination className="pagination-centered mt-3">
                  {Array.from(
                    { length: Math.ceil(votes.length / votesPerPage) },
                    (_, index) => (
                      <Pagination.Item key={index + 1} onClick={() => paginate(index + 1)}>
                        {index + 1}
                      </Pagination.Item>
                    )
                  )}
                </Pagination>
              </div>
            </Collapse>
          </>
        )}
      </div>
    </div>
  );
};

const RatingStars = ({
  count = 5,
  averageValue = 0,
  userValue = 0,
  size = 30,
  activeColor = '#ffd700',
  onChange,
}) => {
  return (
    <div className="d-flex justify-content-center">
      {Array.from({ length: count }, (_, index) => (
        <FaStar
          key={index}
          size={size}
          color={index < (userValue || averageValue) ? activeColor : '#e4e5e9'}
          style={{ cursor: onChange ? 'pointer' : 'default' }}
          onClick={() => {
            if (onChange) onChange(index + 1);
          }}
        />
      ))}
    </div>
  );
};

export default VoteList;
